import type { RRuleWeekday } from '@/modules/timesheets/util'
import { isRRuleWeekday, isRRuleWeekdays } from '@/modules/timesheets/util'
import { addDays } from 'date-fns'
import padStart from 'lodash/padStart'
import { format } from './date-fns'

/**
 * Makes the thread sleep for the given amount of milliseconds
 * @param ms
 */
export const sleep = (ms: number) => new Promise(res => setTimeout(res, ms))

/**
 * Returns the given amount of seconds to a formatted string of "hh:mm"
 * @param durationAsSeconds
 */
export function durationAsSecondsToString(durationAsSeconds: number) {
  const hoursToReturn = padStart(
    Math.floor(durationAsSeconds / 3600).toString(),
    2,
    '0'
  )
  const minutesToReturn = padStart(
    Math.floor((durationAsSeconds / 60) % 60).toString(),
    2,
    '0'
  )

  return `${hoursToReturn}:${minutesToReturn}`
}

/**
 * Returns the given amount of seconds as minutes
 * @param durationAsSeconds
 */
export function durationAsSecondsToMinutes(durationAsSeconds: number) {
  return Math.floor(durationAsSeconds / 60)
}

/**
 * Returns the given amount of seconds as hours
 * @param durationAsSeconds
 */
export function durationAsSecondsToHours(durationAsSeconds: number) {
  return Math.floor(durationAsSeconds / 3600)
}

/**
 * Returns full amount of minutes in date, discounting year, month and date
 * @param date Date to parse
 */
export function getFullMinutes(date: Date) {
  return date.getHours() * 60 + date.getMinutes()
}
/**
 * Get number of hours with decimal
 * @param date Date to parse
 */
export function getHoursDecimal(date: Date) {
  return date.getHours() + date.getMinutes() / 60
}

export type TimePrecision = 'hours' | 'minutes' | 'seconds'

/**
 * Convert string of format hh:mm to total of selected precision
 * @param hhmm Time string in format hh:mm (24-hour)
 * @param precision Return precision, default "seconds"
 * @returns floored numeric of given precision, or null if invalid time string
 */
export function hhmmToNumeric(
  hhmm: string,
  precision: TimePrecision = 'seconds'
) {
  if (!/^\d{2,}:\d{2}$/.test(hhmm)) return null

  const [hours = 0, minutes = 0] = hhmm
    .split(':')
    .map(value => parseInt(value, 10))

  switch (precision) {
    case 'seconds':
      return hours * 3600 + minutes * 60
    case 'minutes':
      return hours * 60 + minutes
    case 'hours':
      return hours
  }
}

/**
 * Graphene omits the seconds from the string if its value is 0. This function
 * handles both cases and parses the value to the provided format
 * @param timeDelta Time string, can either be in HH:mm or HH:mm:ss format
 * @param precision Return precision, default "seconds"
 * @returns roofed numeric of given precision, or null if invalid time string
 */
export function grapheneTimeDeltaToNumeric(
  hhmm: string,
  precision: TimePrecision = 'seconds'
) {
  const oneColonCheck = /^\d{2,}:\d{2}$/.test(hhmm)
  const twoColonCheck = /^\d{2,}:\d{2}:\d{2}$/.test(hhmm)

  if (!oneColonCheck && !twoColonCheck) return null

  const [hours = 0, minutes = 0, seconds = 0] = hhmm
    .split(':')
    .map(value => parseInt(value, 10))

  switch (precision) {
    case 'seconds':
      return hours * 3600 + minutes * 60 + seconds
    case 'minutes': {
      const secondsToMin = seconds > 0 ? 1 : 0
      return hours * 60 + minutes + secondsToMin
    }
    case 'hours': {
      const minutesToHour = minutes > 30 ? 1 : 0
      return hours + minutesToHour
    }
  }
}

/**
 * Check if a date is an invalid date
 * @param date Date to check
 * @param fallback Fallback date
 * @returns date or fallback if fallback is given, otherwise boolean
 */
export function isInvalidDate(date: Date): boolean
export function isInvalidDate(date: Date, fallback: Date): Date
export function isInvalidDate(date: Date, fallback?: Date) {
  const invalid = isNaN(date.getTime())

  if (typeof fallback !== 'undefined') return invalid ? fallback : date

  return invalid
}

export function isValidDateRange({ from, to }: { from: Date; to: Date }) {
  if (!isInvalidDate(from) && !isInvalidDate(to) && from <= to) return true
  return false
}

export function minDate(...dates: Date[]) {
  return new Date(Math.min(...dates.map(date => date.getTime())))
}

export function maxDate(...dates: Date[]) {
  return new Date(Math.max(...dates.map(date => date.getTime())))
}

export function combineDateAndTime(date: Date, time: Date) {
  const combined = new Date(date)
  combined.setHours(
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    time.getMilliseconds()
  )
  return combined
}

export function getMonday(date?: Date) {
  if (!date) date = new Date()
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1) // adjust when day is sunday
  return new Date(date.setDate(diff))
}

const WEEKDAY_BIT: Record<RRuleWeekday, number> = {
  MO: 1,
  TU: 2,
  WE: 4,
  TH: 8,
  FR: 16,
  SA: 32,
  SU: 64,
}

export function weekdaysToBitmask(weekdays: string[]): number {
  if (!isRRuleWeekdays(weekdays)) return 0
  return weekdays.reduce((acc, day) => acc | (WEEKDAY_BIT[day] ?? 0), 0)
}

export function bitmaskToWeekdays(bitmask: number): RRuleWeekday[] {
  const weekdays: RRuleWeekday[] = []
  for (const [day, bit] of Object.entries(WEEKDAY_BIT)) {
    if (!isRRuleWeekday(day)) continue
    if ((bitmask & bit) !== 0) weekdays.push(day)
  }

  return weekdays
}

/**
 * Get translated weekday name
 * @param day 0 = Monday, 6 = Sunday
 * @param form 'sm' = short (Mo), 'md' = medium (Mon), 'lg' = long (Monday)
 */
export function getWeekdayName(day: number, form: 'sm' | 'md' | 'lg'): string {
  const date = addDays(getMonday(), day)
  const frmt = form === 'sm' ? 'EE' : form === 'md' ? 'EEE' : 'EEEE'
  return format(date, frmt)
}
